import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { refertiAPI } from '../../services/refertiApi';
import { apriFile } from '../../services/fileUrl';

/**
 * TabellaEsami — la tabella cumulativa degli esami di laboratorio (Alex, 28
 * settembre 2026): la prima cosa che si vede aprendo gli esami.
 *
 * Righe: gli analiti, raggruppati come nel referto (emocromo, lipidi...).
 * Colonne: i prelievi, il piu' recente a sinistra. Cella: valore e unita'.
 *
 * LE CONVERSIONI LE FA IL SERVER (GET /referti/cumulativa): qui si mostra.
 * Un valore convertito porta l'originale nel suggerimento; uno senza
 * conversione resta nella sua unita' ed e' segnato. NIENTE GIUDIZI: nessun
 * colore alto/basso, nessun intervallo dal catalogo (regola 11 dell'MDR); il
 * riferimento e il segno fuori norma si mostrano solo se li ha scritti il
 * laboratorio, e l'estrazione li ha letti, con la loro fonte.
 *
 * Un valore non verificato e' segnato «da verificare»: il clic porta alla
 * verifica. Un valore verificato: il clic apre il PDF da cui viene.
 */
const NOMI_GRUPPO = {
    ematologia: 'Emocromo', coagulazione: 'Coagulazione', biochimica: 'Biochimica', lipidi: 'Assetto lipidico',
    fegato: 'Funzione epatica', rene: 'Funzione renale', elettroliti: 'Elettroliti', tiroide: 'Tiroide',
    ormoni: 'Ormoni', ferro: 'Metabolismo del ferro', vitamine: 'Vitamine', infiammazione: 'Infiammazione',
    urine: 'Urine', 'non riconosciuti': 'Non ancora riconosciuti',
};
const nomeGruppo = (g) => NOMI_GRUPPO[g] || (g ? g.charAt(0).toUpperCase() + g.slice(1) : 'Altro');
const dataIta = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : '—';
};
const numero = (v, decimali) => (v === null || v === undefined ? ''
    : Number(v).toLocaleString('it-IT', { maximumFractionDigits: Number.isInteger(decimali) ? decimali : 3 }));

const TabellaEsami = ({ pazienteId, onVerifica, onAndamento, versione = 0 }) => {
    const [valori, setValori] = useState(null);
    const [soloVerificati, setSoloVerificati] = useState(false);
    const [errore, setErrore] = useState(null);

    useEffect(() => {
        let vivo = true;
        refertiAPI.cumulativa(pazienteId)
            .then((r) => { if (vivo) setValori(r.data.data.valori || []); })
            .catch((e) => { if (vivo) { setValori([]); setErrore(e?.response?.data?.message || 'Non riesco a leggere la tabella degli esami'); } });
        return () => { vivo = false; };
    }, [pazienteId, versione]);

    const { date, gruppi } = useMemo(() => {
        const vv = (valori || []).filter((v) => !soloVerificati || v.verificato);
        const d = [...new Set(vv.map((v) => v.data_prelievo))].sort().reverse();
        const righe = new Map();
        for (const v of vv) {
            const chiave = v.analita_id ? `a${v.analita_id}` : `g${v.analita}`;
            if (!righe.has(chiave)) {
                righe.set(chiave, { chiave, nome: v.analita, codice: v.analita_codice, gruppo: v.categoria,
                                    ordine: v.ordine ?? 9999, riconosciuto: v.riconosciuto, celle: {} });
            }
            const r = righe.get(chiave);
            if (!r.celle[v.data_prelievo]) r.celle[v.data_prelievo] = v;
        }
        const g = new Map();
        [...righe.values()].sort((a, b) => (a.gruppo === 'non riconosciuti') - (b.gruppo === 'non riconosciuti')
            || a.ordine - b.ordine || a.nome.localeCompare(b.nome)).forEach((r) => {
            if (!g.has(r.gruppo)) g.set(r.gruppo, []);
            g.get(r.gruppo).push(r);
        });
        return { date: d, gruppi: [...g.entries()] };
    }, [valori, soloVerificati]);

    if (valori === null) return <div className="esr-attesa">Caricamento…</div>;
    const daVerificare = (valori || []).filter((v) => !v.verificato).length;

    return (
        <section className="esr-tabella-cumulativa">
            <div className="esr-tc-testa">
                <label className="esr-tc-filtro" data-non-trattiene>
                    <input type="checkbox" checked={soloVerificati} onChange={(e) => setSoloVerificati(e.target.checked)} />
                    Solo verificati
                </label>
                {daVerificare > 0 && <span className="esr-tc-da-verificare-conto">{daVerificare} valori da verificare</span>}
            </div>
            {errore && <p className="esr-errore">{errore}</p>}
            {date.length === 0 ? (
                <p className="esr-vuoto">Nessun valore di laboratorio{soloVerificati ? ' verificato' : ''}.</p>
            ) : (
                <div className="rm-scorre esr-tc-scorre">
                    <table className="esr-tc">
                        <thead>
                            <tr>
                                <th className="esr-tc-analita">Esame</th>
                                {date.map((d) => <th key={d} className="num">{dataIta(d)}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {gruppi.map(([gruppo, righe]) => (
                                <React.Fragment key={gruppo}>
                                    <tr className="esr-tc-gruppo"><th className="esr-tc-analita" colSpan={1}>{nomeGruppo(gruppo)}</th>
                                        {date.map((d) => <td key={d} />)}</tr>
                                    {righe.map((r) => (
                                        <tr key={r.chiave}>
                                            <th className="esr-tc-analita" scope="row">
                                                {r.codice && onAndamento ? (
                                                    <button type="button" className="esr-tc-nome" title="Andamento nel tempo"
                                                            onClick={() => onAndamento(r.codice)}>
                                                        {r.nome} <TrendingUp size={12} aria-hidden="true" />
                                                    </button>
                                                ) : r.nome}
                                            </th>
                                            {date.map((d) => {
                                                const v = r.celle[d];
                                                if (!v) return <td key={d} className="num esr-tc-vuota" />;
                                                const testo = v.valore !== null ? `${numero(v.valore, v.decimali)}${v.unita ? ` ${v.unita}` : ''}` : (v.valore_testo || '');
                                                const suggerimento = [
                                                    v.convertito ? `Nel referto: ${numero(v.originale?.valore)} ${v.originale?.unita || ''}` : null,
                                                    v.senza_conversione ? `Unità del referto (${v.unita || '?'}): manca la conversione verso l'unità dell'esame, il valore non è convertito` : null,
                                                    v.riferimento_laboratorio ? `Riferimento del laboratorio: ${v.riferimento_laboratorio}` : null,
                                                    v.verificato ? 'Clic: apre il referto (PDF)' : 'Da verificare — clic: vai alla verifica',
                                                ].filter(Boolean).join('\n');
                                                return (
                                                    <td key={d} className={`num esr-tc-cella ${v.verificato ? '' : 'esr-tc-cella--da-verificare'} ${v.senza_conversione ? 'esr-tc-cella--unita' : ''}`}>
                                                        <button type="button" title={suggerimento}
                                                                onClick={() => (v.verificato
                                                                    ? (v.documento_url ? apriFile(v.documento_url) : onVerifica && onVerifica(v.referto_id))
                                                                    : onVerifica && onVerifica(v.referto_id))}>
                                                            {testo}
                                                            {v.segno_laboratorio && <span className="esr-tc-segno" title="Segno scritto dal laboratorio">{v.segno_laboratorio}</span>}
                                                            {!v.verificato && <span className="esr-tc-etichetta">da verificare</span>}
                                                            {v.senza_conversione && <span className="esr-tc-etichetta">unità del referto</span>}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="esr-tc-nota">
                I riferimenti e i segni fuori norma, quando ci sono, sono quelli scritti dal laboratorio sul referto:
                il gestionale non confronta i valori con nessun intervallo.
            </p>
        </section>
    );
};

export default TabellaEsami;
